/* =========================================================
   Scrolling Gallery (marquee)
   - Duplicates each row into a seamless loop
   - Sets duration from pixels-per-second speed
   - Pauses videos when off-screen; plays when visible
   - Rebuilds on resize / theme-editor reload
   ========================================================= */
(function () {
  "use strict";

  var SELECTOR = "[data-sg]";
  var MOBILE_QUERY = "(max-width: 749px)";

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function reducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function speedFor(section) {
    var isMobile = window.matchMedia(MOBILE_QUERY).matches;
    var d = parseFloat(section.getAttribute("data-speed-desktop")) || 60;
    var m = parseFloat(section.getAttribute("data-speed-mobile")) || d;
    var s = isMobile ? m : d;
    return s > 0 ? s : 1; // px per second
  }

  function primeVideos(scope) {
    var vids = scope.querySelectorAll("video");
    Array.prototype.forEach.call(vids, function (v) {
      v.muted = true;
      v.defaultMuted = true;
      v.setAttribute("muted", "");
      v.setAttribute("playsinline", "");
      v.loop = true;
      var p = v.play && v.play();
      if (p && p.catch) {
        p.catch(function () {});
      }
    });
  }

  function buildRow(row, speed, reduce) {
    var track = row.querySelector(".sg-track");
    var viewport = row.querySelector(".sg-viewport");
    if (!track || !viewport) {
      return;
    }

    // Keep a pristine copy of the original tiles so we can rebuild cleanly.
    if (track.getAttribute("data-base") == null) {
      track.setAttribute("data-base", track.innerHTML);
    } else {
      track.innerHTML = track.getAttribute("data-base");
    }

    var tiles = Array.prototype.slice.call(track.children);
    if (!tiles.length) {
      row.setAttribute("hidden", "");
      return;
    }
    row.removeAttribute("hidden");

    // Reduced motion: no loop clone, just leave the (scrollable) originals.
    if (reduce) {
      track.style.removeProperty("--sg-duration");
      primeVideos(track);
      return;
    }

    // Group the originals; repeat them until the group fills the viewport,
    // so short galleries still scroll without visible gaps.
    var group = document.createElement("div");
    group.className = "sg-group";
    tiles.forEach(function (t) {
      group.appendChild(t);
    });
    track.appendChild(group);

    var vw = viewport.clientWidth || 1;
    var guard = 0;
    while (group.scrollWidth < vw + 1 && guard < 60) {
      tiles.forEach(function (t) {
        group.appendChild(t.cloneNode(true));
      });
      guard++;
    }

    var groupWidth = group.scrollWidth;

    // Clone the group once → track is exactly two identical halves,
    // so translateX(-50%) loops seamlessly.
    var clone = group.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    track.appendChild(clone);

    // Consistent speed regardless of tile count.
    var duration = groupWidth / speed;
    if (!isFinite(duration) || duration < 1) {
      duration = 1;
    }
    track.style.setProperty("--sg-duration", duration.toFixed(2) + "s");

    primeVideos(track);
  }

  function attachVideoObserver(section) {
    if (!("IntersectionObserver" in window)) {
      return;
    }

    if (!section._sgIO) {
      section._sgIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            var v = e.target;
            if (e.isIntersecting) {
              var p = v.play && v.play();
              if (p && p.catch) {
                p.catch(function () {});
              }
            } else if (!v.paused) {
              v.pause();
            }
          });
        },
        { threshold: 0.1 },
      );
    } else {
      section._sgIO.disconnect();
    }

    var vids = section.querySelectorAll("video");
    Array.prototype.forEach.call(vids, function (v) {
      section._sgIO.observe(v);
    });
  }

  function init(section) {
    var rows = section.querySelectorAll(".sg-row");
    if (!rows.length) {
      return;
    }

    var reduce = reducedMotion();
    section.classList.toggle("sg--reduced", reduce);

    var speed = speedFor(section);
    Array.prototype.forEach.call(rows, function (row) {
      buildRow(row, speed, reduce);
    });

    attachVideoObserver(section);
  }

  function bootAll() {
    var sections = document.querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(sections, init);
  }

  var onResize = debounce(bootAll, 250);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootAll);
  } else {
    bootAll();
  }

  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);

  // Theme editor: rebuild when this section is re-rendered.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (e) {
      var section = e.target.querySelector(SELECTOR);
      if (!section && e.target.matches && e.target.matches(SELECTOR)) {
        section = e.target;
      }
      if (section) {
        init(section);
      }
    });
  }
})();
